import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  MapPin, Clock, Eye, Heart, Share2, Flag, Phone, MessageCircle,
  ChevronLeft, ChevronRight, Star, User, ArrowLeft, ImageIcon, BadgeCheck,
  Link2, Facebook
} from 'lucide-react';
import { trackPageView } from '../analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ListingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [relatedListings, setRelatedListings] = useState([]);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    trackPageView('listing', id);
    fetchListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchListing = async () => {
    try {
      const res = await axios.get(`${API}/listings/${id}`);
      setListing(res.data);
      
      if (res.data.category_id) {
        const relatedRes = await axios.get(`${API}/listings?category_id=${res.data.category_id}&page_size=4`);
        setRelatedListings(relatedRes.data.filter(l => l.id !== id));
      }
    } catch (error) {
      console.error('Error fetching listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return `${t('common.aed')} ${new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-AE' : 'en-AE').format(price)}`;
  };

  const handleWhatsAppContact = () => {
    const phone = listing.seller_whatsapp || listing.seller_phone;
    if (phone) {
      const message = encodeURIComponent(i18n.language === 'ar' ? `مرحباً، أنا مهتم بـ: ${listing.title}` : `Hi, I'm interested in: ${listing.title}`);
      window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
    }
  };

  const handlePhoneCall = () => {
    if (listing.seller_phone) {
      window.location.href = `tel:${listing.seller_phone}`;
    }
  };

  const shareOptions = [
    { name: t('listing.whatsapp'), icon: <MessageCircle className="w-5 h-5 text-green-600" />, action: () => window.open(`https://wa.me/?text=${encodeURIComponent(`${listing.title} - AED ${listing.price} - ${window.location.href}`)}`, '_blank') },
    { name: 'Facebook', icon: <Facebook className="w-5 h-5 text-blue-600" />, action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank') },
    { name: t('common.copy'), icon: <Link2 className="w-5 h-5 text-gray-600" />, action: () => { navigator.clipboard.writeText(window.location.href); alert(t('common.copied')); } },
  ];

  const handleChat = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }
    
    setSendingMessage(true);
    try {
      // Send initial message to start conversation
      const initialMessage = i18n.language === 'ar' ? `مرحباً، أنا مهتم بـ: ${listing.title}` : `Hi, I'm interested in: ${listing.title}`;
      const res = await axios.post(`${API}/messages`, {
        listing_id: id,
        content: initialMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Navigate to chat
      if (res.data.conversation_id) {
        navigate(`/chat/${res.data.conversation_id}`);
      } else {
        navigate('/chats');
      }
    } catch (error) {
      console.error('Error starting chat:', error);
      if (error.response?.status === 401) {
        navigate('/auth');
      } else {
        alert(t('error.generic'));
      }
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-[#E31C25] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center pb-20">
        <div className="text-center">
          <p className="text-xl text-gray-500 mb-4">{t('error.notFound')}</p>
          <button onClick={() => navigate('/')} className="text-[#E31C25] font-semibold">{t('admin.goHome')}</button>
        </div>
      </div>
    );
  }

  const images = listing.images || [];

  return (
    <div className="min-h-screen bg-[#F8F9FA] pb-24 md:pb-8">
      {/* Share Modal Overlay for Mobile */}
      {showShareMenu && (
        <div className="fixed inset-0 z-[100] md:hidden" onClick={() => setShowShareMenu(false)}>
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute bottom-28 left-4 right-4 bg-white rounded-2xl p-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">{t('listing.share')}</h3>
            <div className="grid grid-cols-3 gap-4">
              {shareOptions.map((option, index) => (
                <button
                  key={index}
                  onClick={() => { option.action(); setShowShareMenu(false); }}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50"
                  data-testid={`share-${option.name.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center">{option.icon}</div>
                  <span className="text-sm text-gray-700">{option.name}</span>
                </button>
              ))}
            </div>
            <button 
              onClick={() => setShowShareMenu(false)}
              className="w-full mt-4 py-3 bg-gray-100 rounded-lg font-semibold text-gray-700"
              data-testid="share-cancel"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Mobile Header - Dark */}
      <div className="sticky top-0 z-40 md:hidden hero-gradient">
        <div className="flex items-center justify-between px-4 py-3 relative z-10">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-2">
            <button className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" onClick={() => setShowShareMenu(!showShareMenu)} data-testid="share-btn-mobile">
              <Share2 className="w-5 h-5 text-white" />
            </button>
            <button className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="fav-btn-mobile">
              <Heart className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto">
        {/* Image Gallery */}
        <div className="relative bg-gray-100">
          <div className="aspect-[4/3] md:aspect-[16/9]">
            {images.length > 0 ? (
              <img
                src={images[currentImageIndex].image_url}
                alt={listing.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="w-16 h-16 text-gray-300" />
              </div>
            )}
          </div>
          
          {images.length > 1 && (
            <>
              <button
                onClick={() => setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-md"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-md"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      currentImageIndex === index ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
          
          {listing.is_featured && (
            <span className="absolute top-4 left-4 px-3 py-1 bg-[#E31C25] text-white text-sm font-bold rounded">
              {t('listing.featured')}
            </span>
          )}
          
          {images.length > 0 && (
            <div className="absolute bottom-4 right-4 px-2 py-1 bg-black/60 text-white text-sm rounded flex items-center gap-1">
              <ImageIcon className="w-4 h-4" />
              {currentImageIndex + 1}/{images.length}
            </div>
          )}
        </div>

        <div className="px-4 py-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4">
              {/* Title & Price */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100">
                <div className="flex items-start justify-between mb-2">
                  <h1 className="text-xl font-bold text-gray-900 font-heading" data-testid="listing-title">
                    {listing.title}
                  </h1>
                </div>
                <p className="text-2xl font-extrabold text-[#E31C25] mb-3" data-testid="listing-price">
                  {formatPrice(listing.price)}
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{listing.location?.city || 'UAE'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{new Date(listing.created_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-AE' : 'en-AE')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    <span>{listing.views_count} {t('listing.views')}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100">
                <h2 className="font-bold text-gray-900 mb-3">{t('listing.description')}</h2>
                <p className="text-gray-700 whitespace-pre-line" data-testid="listing-description">
                  {listing.description}
                </p>
              </div>

              {/* Details */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100">
                <h2 className="font-bold text-gray-900 mb-3">{t('listing.details')}</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('filter.category')}</p>
                    <p className="font-medium text-gray-900">{listing.category?.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('listing.condition')}</p>
                    <p className="font-medium text-gray-900 capitalize">{listing.condition === 'new' ? t('listing.new') : listing.condition === 'used' ? t('listing.used') : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('listing.location')}</p>
                    <p className="font-medium text-gray-900">{listing.location?.city || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('listing.posted')}</p>
                    <p className="font-medium text-gray-900">{new Date(listing.created_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-AE' : 'en-AE')}</p>
                  </div>
                  {(() => {
                    try {
                      const meta = typeof listing.listing_metadata === 'string' ? JSON.parse(listing.listing_metadata) : (listing.listing_metadata || {});
                      return Object.entries(meta).filter(([_, v]) => v).map(([key, value]) => (
                        <div key={key}>
                          <p className="text-sm text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                          <p className="font-medium text-gray-900">{value}</p>
                        </div>
                      ));
                    } catch { return null; }
                  })()}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Seller Card */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 bg-[#FEE2E2] rounded-full flex items-center justify-center">
                    <User className="w-7 h-7 text-[#E31C25]" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900" data-testid="seller-name">
                      {listing.seller_name || listing.user?.full_name || t('listing.seller')}
                    </p>
                    <p className="text-sm text-gray-500">{t('listing.memberSince')} {new Date(listing.user?.created_at || listing.created_at).getFullYear()}</p>
                  </div>
                </div>

                <div className="space-y-2.5">
                  <button
                    onClick={handleWhatsAppContact}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors"
                    data-testid="whatsapp-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>{t('listing.whatsapp')}</span>
                  </button>

                  <button
                    onClick={handlePhoneCall}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#1A1A2E] text-white rounded-xl font-semibold hover:bg-[#16213E] transition-colors"
                    data-testid="call-btn"
                  >
                    <Phone className="w-5 h-5" />
                    <span>{t('listing.call')}</span>
                  </button>

                  <button
                    onClick={handleChat}
                    disabled={sendingMessage}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    data-testid="chat-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>{sendingMessage ? t('common.loading') : t('listing.chat')}</span>
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="hidden md:block bg-white rounded-2xl p-4 border border-gray-100">
                <div className="flex items-center justify-between">
                  <button className="flex items-center gap-2 text-gray-600 hover:text-[#E31C25]" data-testid="save-btn">
                    <Heart className="w-5 h-5" />
                    <span>{t('listing.saveAd')}</span>
                  </button>
                  <div className="relative">
                    <button 
                      onClick={() => setShowShareMenu(!showShareMenu)}
                      className="flex items-center gap-2 text-gray-600 hover:text-[#E31C25]"
                      data-testid="share-btn"
                    >
                      <Share2 className="w-5 h-5" />
                      <span>{t('listing.share')}</span>
                    </button>
                    
                    {showShareMenu && (
                      <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50">
                        {shareOptions.map((option, index) => (
                          <button
                            key={index}
                            onClick={() => { option.action(); setShowShareMenu(false); }}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                          >
                            <span>{option.icon}</span>
                            <span>{option.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button className="flex items-center gap-2 text-gray-600 hover:text-[#E31C25]" data-testid="report-btn">
                    <Flag className="w-5 h-5" />
                    <span>{t('listing.reportAd')}</span>
                  </button>
                </div>
              </div>

              {/* Safety Tips */}
              <div className="bg-[#1A1A2E] rounded-2xl p-4">
                <h3 className="font-bold text-white mb-2">{t('listing.safetyTips')}</h3>
                <ul className="text-sm text-white/60 space-y-1">
                  <li>• {t('listing.safetyTip1')}</li>
                  <li>• {t('listing.safetyTip2')}</li>
                  <li>• {t('listing.safetyTip3')}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Related Listings */}
          {relatedListings.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-bold text-gray-900 font-heading mb-4">{t('listing.relatedListings')}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {relatedListings.map((related) => (
                  <Link
                    key={related.id}
                    to={`/listing/${related.id}`}
                    className="bg-white rounded-2xl overflow-hidden border border-gray-100 listing-hover"
                  >
                    <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                      {related.images?.[0] ? (
                        <img
                          src={related.images[0].thumbnail_url || related.images[0].image_url}
                          alt={related.title}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                          <ImageIcon className="w-8 h-8 text-gray-200" />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-[15px] font-extrabold text-[#E31C25] mb-1">{formatPrice(related.price)}</p>
                      <h3 className="font-medium text-gray-800 line-clamp-1 text-sm">{related.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListingDetail;
